import { useMemo, useState } from "react";

import { noop } from "lodash";

import useIsomorphicLayoutEffect from "@/hooks/useIsomorphicLayoutEffect";

export type UseMeasureRect = Pick<
  DOMRectReadOnly,
  "x" | "y" | "top" | "left" | "right" | "bottom" | "height" | "width"
>;
export type UseMeasureRef<E extends Element = Element> = (element: E) => void;
export type UseMeasureResult<E extends Element = Element> = [
  UseMeasureRef<E>,
  UseMeasureRect,
];

const defaultState: UseMeasureRect = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
};

const useMeasure = <E extends Element = HTMLDivElement>(
  callback?: (rect: UseMeasureRect) => void,
): UseMeasureResult<E> => {
  const [element, ref] = useState<E | null>(null);
  const [rect, setRect] = useState<UseMeasureRect>(defaultState);

  const observer = useMemo(
    () =>
      new window.ResizeObserver((entries) => {
        if (entries[0]) {
          const { x, y, width, height, top, left, bottom, right } =
            entries[0].contentRect;
          const newRect = { x, y, width, height, top, left, bottom, right };
          setRect(newRect);
          callback?.(newRect);
        }
      }),
    [callback],
  );

  useIsomorphicLayoutEffect(() => {
    if (!element) return;

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [element, observer]);

  return [ref, rect];
};

export default typeof window !== "undefined" &&
typeof window.ResizeObserver !== "undefined"
  ? useMeasure
  : ((() => [noop, defaultState]) as typeof useMeasure);
